import { supabase } from '../lib/supabase';

// Utility function to convert human-readable time to PostgreSQL time format
const convertTimeToPostgresFormat = (timeString) => {
  if (!timeString) return null;

  // If already in HH:MM:SS or HH:MM format, return as-is
  if (/^\d{1,2}:\d{2}(:\d{2})?$/?.test(timeString?.trim())) {
    // Add seconds if missing
    const time = timeString?.trim();
    return time?.length === 5 ? `${time}:00` : time;
  }

  // Convert human-readable times to 24-hour format
  const timeMap = {
    // Early morning
    'early morning': '06:00:00',
    'dawn': '06:30:00',
    'sunrise': '07:00:00',
    
    // Morning
    'morning': '09:00:00',
    'mid morning': '10:00:00',
    'late morning': '11:00:00',
    
    // Noon/Afternoon
    'noon': '12:00:00',
    'midday': '12:00:00',
    'lunch': '12:30:00',
    'afternoon': '14:00:00',
    'mid afternoon': '15:00:00',
    'late afternoon': '16:00:00',
    
    // Evening
    'evening': '18:00:00',
    'dinner': '19:00:00',
    'dusk': '19:30:00',
    'sunset': '20:00:00',
    
    // Night
    'night': '21:00:00',
    'late night': '22:00:00'
  };

  const lowerTime = timeString?.toLowerCase()?.trim();
  
  // Check for exact matches first
  if (timeMap?.[lowerTime]) {
    return timeMap?.[lowerTime];
  }

  // Check for partial matches
  for (const [key, value] of Object.entries(timeMap)) {
    if (lowerTime?.includes(key)) {
      return value;
    }
  }

  // Try to extract time from strings like "8:00 AM" or "2:30 PM"
  const timeRegex = /(\d{1,2}):?(\d{2})?\s*(am|pm)/i;
  const match = timeString?.match(timeRegex);
  
  if (match) {
    let hour = parseInt(match?.[1]);
    const minute = match?.[2] ? parseInt(match?.[2]) : 0;
    const ampm = match?.[3]?.toLowerCase();

    // Convert to 24-hour format
    if (ampm === 'pm' && hour !== 12) {
      hour += 12;
    } else if (ampm === 'am' && hour === 12) {
      hour = 0;
    }

    return `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}:00`;
  }

  // Try to extract just hour numbers (like "8" or "14")
  const hourMatch = timeString?.match(/^\d{1,2}$/);
  if (hourMatch) {
    let hour = parseInt(hourMatch?.[0]);
    if (hour >= 0 && hour <= 23) {
      return `${hour?.toString()?.padStart(2, '0')}:00:00`;
    }
  }

  // Default fallback based on common words
  if (lowerTime?.includes('early')) return '07:00:00';
  if (lowerTime?.includes('mid')) return '12:00:00';
  if (lowerTime?.includes('late')) return '17:00:00';

  // If no match found, return null (PostgreSQL will accept null for optional time fields)
  console.warn(`Could not parse time: "${timeString}". Setting to null.`);
  return null;
};

export const itineraryService = {
  // Create multiple itinerary items for a trip
  async createTripItinerary(tripId, itineraryItems) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case for database with proper time formatting
    const dbItems = itineraryItems?.map(item => ({
      trip_id: tripId,
      day_number: item?.dayNumber,
      title: item?.title,
      description: item?.description,
      start_time: convertTimeToPostgresFormat(item?.startTime),
      end_time: convertTimeToPostgresFormat(item?.endTime),
      location: item?.location,
      activity_type: item?.activityType,
      cost: item?.cost,
      notes: item?.notes
    }));

    const { data, error } = await supabase?.from('trip_itinerary')?.insert(dbItems)?.select();

    if (error) throw error;
    
    return data?.map(item => ({
      id: item?.id,
      tripId: item?.trip_id,
      dayNumber: item?.day_number,
      title: item?.title,
      description: item?.description,
      startTime: item?.start_time,
      endTime: item?.end_time,
      location: item?.location,
      activityType: item?.activity_type,
      cost: item?.cost,
      notes: item?.notes,
      createdAt: item?.created_at,
      updatedAt: item?.updated_at
    }));
  },

  // Get itinerary items for a trip
  async getTripItinerary(tripId) {
    const { data, error } = await supabase?.from('trip_itinerary')?.select('*')?.eq('trip_id', tripId)?.order('day_number', { ascending: true })?.order('start_time', { ascending: true });

    if (error) throw error;
    
    return data?.map(item => ({
      id: item?.id,
      tripId: item?.trip_id,
      dayNumber: item?.day_number,
      title: item?.title,
      description: item?.description,
      startTime: item?.start_time,
      endTime: item?.end_time,
      location: item?.location,
      activityType: item?.activity_type,
      cost: item?.cost,
      notes: item?.notes,
      createdAt: item?.created_at,
      updatedAt: item?.updated_at
    }));
  },

  // Update itinerary item
  async updateItineraryItem(itemId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case with proper time formatting
    const updateData = {};
    if (updates?.dayNumber !== undefined) updateData.day_number = updates?.dayNumber;
    if (updates?.title !== undefined) updateData.title = updates?.title;
    if (updates?.description !== undefined) updateData.description = updates?.description;
    if (updates?.startTime !== undefined) updateData.start_time = convertTimeToPostgresFormat(updates?.startTime);
    if (updates?.endTime !== undefined) updateData.end_time = convertTimeToPostgresFormat(updates?.endTime);
    if (updates?.location !== undefined) updateData.location = updates?.location;
    if (updates?.activityType !== undefined) updateData.activity_type = updates?.activityType;
    if (updates?.cost !== undefined) updateData.cost = updates?.cost;
    if (updates?.notes !== undefined) updateData.notes = updates?.notes;

    const { data, error } = await supabase?.from('trip_itinerary')?.update(updateData)?.eq('id', itemId)?.select()?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      tripId: data?.trip_id,
      dayNumber: data?.day_number,
      title: data?.title,
      description: data?.description,
      startTime: data?.start_time,
      endTime: data?.end_time,
      location: data?.location,
      activityType: data?.activity_type,
      cost: data?.cost,
      notes: data?.notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete itinerary item
  async deleteItineraryItem(itemId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('trip_itinerary')?.delete()?.eq('id', itemId);

    if (error) throw error;
  },

  // Convert AI-generated trip data to database format with proper time handling
  convertAITripToItinerary(aiTripData) {
    const itineraryItems = [];

    aiTripData?.itinerary?.forEach((day, dayIndex) => {
      day?.activities?.forEach((activity, activityIndex) => {
        itineraryItems?.push({
          dayNumber: dayIndex + 1,
          title: activity?.title,
          description: activity?.description,
          startTime: activity?.time, // This will be converted by convertTimeToPostgresFormat in createTripItinerary
          endTime: activity?.endTime, // This will be converted by convertTimeToPostgresFormat in createTripItinerary
          location: activity?.location,
          activityType: activity?.activityType || 'General',
          cost: activity?.cost || null,
          notes: activity?.essentialItems?.join(', ') || null
        });
      });
    });

    return itineraryItems;
  }
};